import React, { useState } from 'react'
import { AlertTriangle, Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import ConnectAiToolsPanel from './ConnectAiToolsPanel'

// Shown once right after a token is created — the only time the plaintext is available.
const TokenRevealDialog = ({ token, apiToken, meta, onClose }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true)
      toast.success('Token copied')
    })
  }

  const handleOpenChange = (open) => {
    if (open) return
    if (!copied && !window.confirm("You haven't copied the token. It will not be shown again. Close anyway?")) return
    onClose()
  }

  return (
    <Dialog open={!!token} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Token "{apiToken?.name}" created</DialogTitle>
          <DialogDescription>Copy it now and store it somewhere safe.</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>This is the only time the token is shown. Anyone with it can act as you within its permissions — never commit it to git or paste it in chats.</span>
        </div>

        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono bg-muted rounded-md px-3 py-2 break-all">{token}</code>
          <Button size="sm" variant={copied ? 'secondary' : 'default'} onClick={handleCopy}>
            {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Connect your AI tool</h3>
          <ConnectAiToolsPanel mcpUrl={meta?.mcp_url} apiUrl={meta?.api_url} token={token} />
        </div>

        <DialogFooter>
          <Button onClick={() => handleOpenChange(false)}>{copied ? "Done, I've saved it" : 'Close'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TokenRevealDialog
